import { useEffect, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import PageShell from "@/components/layout/PageShell";
import MasterTable from "@/components/MasterTable";
import { Building2, Plus, Pencil, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", gstin: "", address: "", active: true };

export default function MasterCompaniesPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    try { setItems((await api.get("/companies")).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setEditId(r.id);
    setForm({ name: r.name || "", gstin: r.gstin || "", address: r.address || "", active: r.active !== false });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Company name is required");
    try {
      if (editId) await api.patch(`/companies/${editId}`, form);
      else await api.post("/companies", form);
      toast.success(editId ? "Company updated" : "Company added");
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (r) => {
    if (r.is_default) return toast.error("The Default company cannot be deleted — rename it instead.");
    if (!window.confirm(`Delete "${r.name}"? Employees & cost centres will move to Default.`)) return;
    try { await api.delete(`/companies/${r.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <PageShell title="Companies" subtitle="Legal entities under this tenant · powers Company-level billing authentication" overline="Master · Companies">
      <div className="flex justify-end mb-3">
        <Button data-testid="co-new" onClick={openCreate} className="rounded-md bg-primary text-primary-foreground">
          <Plus size={13} className="mr-1"/> Add company
        </Button>
      </div>
      <MasterTable
        columns={[
          { key: "name", label: "Company", render: (r) => (
              <div className="flex items-center gap-2">
                <Building2 size={13} className="text-primary"/>
                <span className="font-semibold">{r.name}</span>
                {r.is_default && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-muted rounded border border-border">Default</span>}
              </div>
          )},
          { key: "gstin", label: "GSTIN", className: "mono text-xs", render: (r) => r.gstin || "—" },
          { key: "address", label: "Address", className: "text-xs", render: (r) => r.address || "—" },
          { key: "active", label: "Status", render: (r) => (
              <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 mono ${
                r.active ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-slate-400 text-slate-500 bg-slate-50"
              }`}>{r.active ? "Active" : "Inactive"}</span>
          )},
          { key: "actions", label: "", sortable: false, className: "text-right whitespace-nowrap", render: (r) => (
              <div className="inline-flex items-center gap-3">
                <button data-testid={`co-edit-${r.id}`} onClick={() => openEdit(r)} className="text-muted-foreground hover:text-primary"><Pencil size={13}/></button>
                {!r.is_default && (
                  <button data-testid={`co-del-${r.id}`} onClick={() => remove(r)} className="text-destructive hover:text-primary"><Trash2 size={13}/></button>
                )}
              </div>
          )},
        ]}
        rows={items}
        searchKeys={["name", "gstin", "address"]}
        getRowKey={(r) => r.id}
        testidPrefix="co"
        defaultSort={{ key: "name", dir: "asc" }}
        emptyText="No companies yet. Every tenant starts with a Default company — add real legal entities here."
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-md max-w-lg" data-testid="co-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit company" : "Add company"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <label className="block">
              <span className="overline">Company name *</span>
              <Input data-testid="co-name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} required className="mt-1 rounded-md" />
            </label>
            <label className="block">
              <span className="overline">GSTIN</span>
              <Input data-testid="co-gstin" value={form.gstin} onChange={(e)=>setForm({...form, gstin:e.target.value.toUpperCase()})} placeholder="15-digit GSTIN" className="mt-1 rounded-md mono" maxLength={15} />
            </label>
            <label className="block">
              <span className="overline">Registered address</span>
              <textarea data-testid="co-address" value={form.address} onChange={(e)=>setForm({...form, address:e.target.value})} rows={2} className="mt-1 w-full rounded-md border border-input p-2 text-sm bg-background" />
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e)=>setForm({...form, active:e.target.checked})} data-testid="co-active" className="h-4 w-4 accent-[#BE1E1E]" />
              <span>Active</span>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={()=>setOpen(false)} className="rounded-md"><X size={13} className="mr-1"/>Cancel</Button>
              <Button type="submit" data-testid="co-submit" className="rounded-md bg-primary text-primary-foreground">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
